/**
 * Admin Home layout (Customize home).
 *
 * Mirrors zephix-backend/src/modules/users/dto/admin-home-layout.dto.ts. A
 * layout is a DISPLAY preference for a page that is already Admin-only: it can
 * hide or reorder sections, never reveal one. Only sections that exist today
 * are listed — nothing here is a placeholder for an unbuilt module.
 *
 * `needs_attention` is PINNED: always rendered at the top, never stored in
 * `sections`, and never hideable. `sections` is the visible hideable sections
 * in display order and may be empty.
 */

export const ADMIN_HOME_PINNED_SECTION_IDS = ["needs_attention"] as const;
export type AdminHomePinnedSectionId =
  (typeof ADMIN_HOME_PINNED_SECTION_IDS)[number];

export const ADMIN_HOME_SECTION_IDS = [
  "quick_actions",
  "workspaces",
  "projects_at_risk",
  "setup_checklist",
  "people_by_role",
] as const;
export type AdminHomeSectionId = (typeof ADMIN_HOME_SECTION_IDS)[number];

export const ADMIN_HOME_ACTION_IDS = [
  "create_workspace",
  "template_center",
  "governance_gate",
  "invite_member",
] as const;
export type AdminHomeActionId = (typeof ADMIN_HOME_ACTION_IDS)[number];

export const ADMIN_HOME_LAYOUT_VERSION = 1 as const;

export type AdminHomeLayout = {
  v: typeof ADMIN_HOME_LAYOUT_VERSION;
  sections: AdminHomeSectionId[];
  /** Visible quick actions, in the order the buttons appear. */
  actions: AdminHomeActionId[];
};

type Meta = { label: string; description: string };

export const ADMIN_HOME_PINNED_SECTION_META: Record<
  AdminHomePinnedSectionId,
  Meta
> = {
  needs_attention: {
    label: "Needs your attention",
    description: "Gates and invitations waiting on you.",
  },
};

export const ADMIN_HOME_SECTION_META: Record<AdminHomeSectionId, Meta> = {
  quick_actions: {
    label: "Quick actions",
    description: "Shortcuts for common admin tasks.",
  },
  workspaces: {
    label: "Workspaces",
    description: "Every workspace and its project count.",
  },
  projects_at_risk: {
    label: "Projects at risk",
    description: "Projects with at least one active risk.",
  },
  setup_checklist: {
    label: "Setup checklist",
    description: "Progress through organization setup.",
  },
  people_by_role: {
    label: "People by role",
    description: "Headcount by Admin, Member, and Viewer.",
  },
};

export const ADMIN_HOME_ACTION_META: Record<AdminHomeActionId, Meta> = {
  create_workspace: {
    label: "Create Workspace",
    description: "Open the new workspace form.",
  },
  template_center: {
    label: "Template Center",
    description: "Browse project templates.",
  },
  governance_gate: {
    label: "Governance exceptions",
    description: "Open governance exceptions.",
  },
  invite_member: {
    label: "Invite Member",
    description: "Invite people to the organization.",
  },
};

export const DEFAULT_ADMIN_HOME_LAYOUT: AdminHomeLayout = {
  v: ADMIN_HOME_LAYOUT_VERSION,
  sections: [
    "quick_actions",
    "workspaces",
    "projects_at_risk",
    "setup_checklist",
    "people_by_role",
  ],
  actions: [...ADMIN_HOME_ACTION_IDS],
};

function cleanIds<T extends string>(
  raw: unknown,
  known: readonly T[],
): T[] | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<string>();
  const out: T[] = [];
  for (const id of raw) {
    if (
      typeof id !== "string" ||
      !(known as readonly string[]).includes(id) ||
      seen.has(id)
    ) {
      return null;
    }
    seen.add(id);
    out.push(id as T);
  }
  return out;
}

/**
 * Accepts anything the API might return. An invalid or missing value yields the
 * default layout, so a bad stored value can never blank the page.
 */
export function parseAdminHomeLayout(raw: unknown): AdminHomeLayout {
  if (raw === null || typeof raw !== "object") return DEFAULT_ADMIN_HOME_LAYOUT;
  const c = raw as Record<string, unknown>;
  if (c.v !== ADMIN_HOME_LAYOUT_VERSION) return DEFAULT_ADMIN_HOME_LAYOUT;
  const sections = cleanIds(c.sections, ADMIN_HOME_SECTION_IDS);
  const actions = cleanIds(c.actions, ADMIN_HOME_ACTION_IDS);
  if (!sections || !actions) return DEFAULT_ADMIN_HOME_LAYOUT;
  return { v: ADMIN_HOME_LAYOUT_VERSION, sections, actions };
}

/** Sections not currently shown, in canonical order. */
export function hiddenSections(layout: AdminHomeLayout): AdminHomeSectionId[] {
  return ADMIN_HOME_SECTION_IDS.filter((id) => !layout.sections.includes(id));
}

export function showSection(
  layout: AdminHomeLayout,
  id: AdminHomeSectionId,
): AdminHomeLayout {
  if (layout.sections.includes(id)) return layout;
  return { ...layout, sections: [...layout.sections, id] };
}

export function hideSection(
  layout: AdminHomeLayout,
  id: AdminHomeSectionId,
): AdminHomeLayout {
  if (!layout.sections.includes(id)) return layout;
  return { ...layout, sections: layout.sections.filter((x) => x !== id) };
}

/**
 * Moves a visible section one step. `isShown` lets the caller skip sections
 * that are visible in the layout but not rendered (for example the checklist
 * when onboarding data failed to load), so an arrow never swaps with an
 * invisible neighbour.
 */
export function moveSection(
  layout: AdminHomeLayout,
  id: AdminHomeSectionId,
  direction: -1 | 1,
  isShown: (id: AdminHomeSectionId) => boolean = () => true,
): AdminHomeLayout {
  const from = layout.sections.indexOf(id);
  if (from === -1) return layout;
  let to = from + direction;
  while (
    to >= 0 &&
    to < layout.sections.length &&
    !isShown(layout.sections[to])
  ) {
    to += direction;
  }
  if (to < 0 || to >= layout.sections.length) return layout;
  const sections = [...layout.sections];
  [sections[from], sections[to]] = [sections[to], sections[from]];
  return { ...layout, sections };
}

/** Turns a quick action on or off; the visible list keeps canonical order. */
export function setActionVisible(
  layout: AdminHomeLayout,
  id: AdminHomeActionId,
  visible: boolean,
): AdminHomeLayout {
  if (layout.actions.includes(id) === visible) return layout;
  const next = new Set<AdminHomeActionId>(layout.actions);
  if (visible) next.add(id);
  else next.delete(id);
  return {
    ...layout,
    actions: ADMIN_HOME_ACTION_IDS.filter((a) => next.has(a)),
  };
}

export function adminHomeLayoutsEqual(
  a: AdminHomeLayout,
  b: AdminHomeLayout,
): boolean {
  const same = (x: readonly string[], y: readonly string[]): boolean =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  return same(a.sections, b.sections) && same(a.actions, b.actions);
}
